// [S-411 / E-MULTI-TENANT] Owns the active-tenant selection + the user's
// membership list, and publishes them on ActiveTenantContext. Wrap the
// authenticated app in this; useSession() then derives the effective per-tenant
// role, so every screen reacts to a switch automatically.
//
// The active tenant is persisted per-uid in localStorage (a tenant id is not a
// secret — unlike the JWT, which the Master Rule keeps in memory only), so a
// reload keeps the user on the workspace they were viewing.
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { useMyMemberships } from "../data/membershipsQuery";
import {
  ActiveTenantContext,
  type ActiveTenantContextValue,
} from "./activeTenantContext";

function storageKey(uid: string): string {
  return `csai-active-tenant:${uid}`;
}

export function ActiveTenantProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const { state } = useAuth();
  const session = state.status === "authenticated" ? state.session : null;
  const uid = session?.uid ?? null;
  // The tenant the user logged into (their token's baked claim) — the natural
  // default before any explicit switch.
  const loginTenantId =
    session?.studioId != null ? `tenant-${session.studioId}` : null;

  const membershipsQuery = useMyMemberships();
  const memberships = useMemo(
    () => membershipsQuery.data ?? [],
    [membershipsQuery.data]
  );

  // Adopt the initial active tenant SYNCHRONOUSLY at mount (persisted choice →
  // login tenant) so the very FIRST render already carries an active-tenant
  // header. Doing this only in the effect below left a one-render window where
  // a deep-linked / hard-refreshed tenant-scoped screen fired its fetch with no
  // active tenant and got a 403 it never retried — e.g. "Draft not available"
  // on a refresh of /drafts/:id, and the same latent race on every other
  // tenant-scoped detail route. The effect still validates/repairs this choice
  // (and handles the global-admin / no-login-tenant case) once memberships load.
  const [activeTenantId, setActiveTenantId] = useState<string | null>(() => {
    if (uid === null) return null;
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(storageKey(uid));
    } catch {
      stored = null;
    }
    return stored ?? loginTenantId;
  });

  // Initialise / re-initialise the active tenant when the user or their
  // membership set changes. Order of preference: persisted choice (if still a
  // valid membership) → login tenant → first membership.
  useEffect(() => {
    if (uid === null) {
      setActiveTenantId(null);
      return;
    }
    const valid = (id: string | null): id is string =>
      id !== null && memberships.some((m) => m.tenantId === id);
    // `loaded` = the memberships query has real data (not the [] it shows while
    // pending or right after switchTenant()'s queryClient.clear()). Only a
    // genuinely loaded list may overrule the current selection.
    const loaded = membershipsQuery.data !== undefined;
    const fallback = (): string | null =>
      (valid(loginTenantId) ? loginTenantId : null) ??
      memberships[0]?.tenantId ??
      loginTenantId;

    // We already hold a selection (initial resolve, a stored choice, or an
    // explicit switchTenant). KEEP it — drop it only if a loaded membership list
    // proves it's no longer a tenant the user belongs to. This is what makes a
    // switch stick through switchTenant()'s cache-clear (which briefly empties
    // memberships) instead of snapping back to the login tenant, while still
    // never re-flipping a valid choice a beat after first paint (the create/send
    // "chat session not found" flap). Explicit switch + uid change still apply.
    if (activeTenantId !== null) {
      if (loaded && !valid(activeTenantId)) setActiveTenantId(fallback());
      return;
    }

    // No selection yet — adopt one IMMEDIATELY (do NOT wait for the memberships
    // fetch). Waiting left a window where chat/precedent requests fired with no
    // active-tenant header and got scoped to the login tenant — which is exactly
    // why chat history stuck to one tenant. Prefer the stored choice, then the
    // login tenant (known from the token, no fetch needed); the keep-branch
    // above validates/repairs it once memberships load.
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(storageKey(uid));
    } catch {
      stored = null;
    }
    const initial =
      stored ??
      loginTenantId ??
      (loaded ? (memberships[0]?.tenantId ?? null) : null);
    if (initial !== null) setActiveTenantId(initial);
  }, [
    uid,
    memberships,
    loginTenantId,
    activeTenantId,
    membershipsQuery.data,
  ]);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const switchTenant = useCallback(
    (tenantId: string) => {
      if (tenantId === activeTenantId) return;
      setActiveTenantId(tenantId);
      if (uid !== null) {
        try {
          window.localStorage.setItem(storageKey(uid), tenantId);
        } catch {
          /* private mode / disabled storage — selection still holds for the session */
        }
      }
      // The previous tenant's cached query data (drafts, templates, precedents,
      // etc.) MUST NOT bleed into the new tenant — many query keys don't include
      // the active tenant, so clear the whole cache, then land on Home so the
      // current page can't keep showing a stale or now-inaccessible entity
      // (e.g. a draft that belongs to the tenant we just left).
      queryClient.clear();
      navigate("/home");
    },
    [activeTenantId, uid, queryClient, navigate]
  );

  const value = useMemo<ActiveTenantContextValue>(
    () => ({
      memberships,
      activeTenantId,
      switchTenant,
      loading: membershipsQuery.isLoading,
    }),
    [memberships, activeTenantId, switchTenant, membershipsQuery.isLoading]
  );

  return (
    <ActiveTenantContext.Provider value={value}>
      {children}
    </ActiveTenantContext.Provider>
  );
}
